import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type BedCountProtocol = 'SA' | 'FWC';

export interface BedCountConfig {
  enabled: boolean;
  protocol: BedCountProtocol;
  curfew: string;            // "HH:MM"
  times: string[];           // ["HH:MM", ...]
  offline_beds: number;
  facility_code: string;
}

export const DEFAULT_BED_COUNT_CONFIG: BedCountConfig = {
  enabled: false,
  protocol: 'SA',
  curfew: '23:00',
  times: ['22:00', '00:00', '02:00'],
  offline_beds: 0,
  facility_code: '',
};

export const SA_DEFAULT_TIMES = ['22:00', '00:00', '02:00'];
export const FWC_DEFAULT_TIMES = ['23:00'];

export const normalizeBedCountTime = (value: string): string | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** Normalize an unknown JSON blob into a fully-populated BedCountConfig. */
export const normalizeBedCountConfig = (raw: any): BedCountConfig => {
  const cfg = raw && typeof raw === 'object' ? raw : {};
  const protocol: BedCountProtocol = cfg.protocol === 'FWC' ? 'FWC' : 'SA';
  return {
    enabled: !!cfg.enabled,
    protocol,
    curfew: typeof cfg.curfew === 'string' ? cfg.curfew : (protocol === 'FWC' ? '23:00' : '22:00'),
    times: Array.isArray(cfg.times) && cfg.times.length > 0
      ? Array.from(new Set(cfg.times.map((t: any) => typeof t === 'string' ? normalizeBedCountTime(t) : null).filter(Boolean) as string[])).sort()
      : (protocol === 'FWC' ? FWC_DEFAULT_TIMES : SA_DEFAULT_TIMES),
    offline_beds: Number.isFinite(cfg.offline_beds) ? Number(cfg.offline_beds) : 0,
    facility_code: typeof cfg.facility_code === 'string' ? cfg.facility_code : '',
  };
};

export const useBedCountConfig = (siteId?: string) => {
  return useQuery({
    queryKey: ['bed-count-config', siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sites')
        .select('bed_count_config, name')
        .eq('id', siteId!)
        .maybeSingle();
      if (error) throw error;
      return {
        config: normalizeBedCountConfig(data?.bed_count_config),
        siteName: data?.name as string | undefined,
      };
    },
  });
};

export const useUpdateBedCountConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, config }: { siteId: string; config: BedCountConfig }) => {
      const { error } = await supabase
        .from('sites')
        .update({ bed_count_config: config as any })
        .eq('id', siteId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['bed-count-config', vars.siteId] });
      qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
};
